/**
 * Карточка единицы оборудования: история обслуживания + форма.
 *
 * Порядок блоков не случаен. Сверху — история, снизу — паспортные поля.
 * Человек открывает карточку, стоя перед сломанной печью: ему нужно «что тут
 * делали в прошлый раз», а не «серийный номер». Паспорт заполняют один раз,
 * историю читают каждый выезд.
 */

import { isActiveTask } from '../utils/taskStatus';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import Icon from './Icon';
import { usePerm } from '../hooks/usePerm';
import { track } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import { UploadSlot, compressImage, FullscreenViewer } from './PhotosSection';
import { formatCode } from '../utils/equipmentCode';
import { useTagSheet } from '../hooks/useTagSheet';
import TaskModal from './TaskModal';
import EquipmentScanner from './EquipmentScanner';
import EquipmentMoveDialog, { MOVE_ERROR_KEYS } from './EquipmentMoveDialog';
import EquipmentStatusBadge from './EquipmentStatusBadge';
import { useTranslatedTask } from '../hooks/useTranslatedTask';
import { useEquipmentTypes, equipmentTypeLabel } from '../hooks/useEquipmentTypes';
import BusyLabel from './BusyLabel';
import EquipmentStatusFlow from './EquipmentStatusFlow';
import EquipmentChecks from './EquipmentChecks';
import { invalidateHasTags } from '../hooks/useHasTags';

const EMPTY = {
  name: '', type: '', manufacturer: '', model: '', serial_no: '',
  installed_at: '', warranty_until: '', notes: '',
  is_mobile: false,
};

// Три разных размера, и каждый под свою задачу:
//
//   PHOTO — общий вид. 1600px хватает, чтобы узнать единицу; в PDF-отчёты он
//           не идёт, поэтому качество отчётное здесь не нужно.
//   THUMB — превью 400px. Ради него всё: карточка и список тянут 30 КБ вместо
//           полумегабайта, а на объекте это разница между «открылось» и
//           «крутится» на мобильном интернете в подвале.
//   PLATE — шильдик. Сжимаем МЕНЬШЕ остальных: модель читает мелкую гравировку,
//           и агрессивный JPEG превращает серийник в кашу, после чего она
//           начинает угадывать.
const PHOTO_MAX = 1600, PHOTO_Q = 0.80;
const THUMB_MAX = 400,  THUMB_Q = 0.72;
const PLATE_MAX = 2000, PLATE_Q = 0.92;

// Фикс, не настройка: было ровно одно фото общего вида, стало — до четырёх.
// Больше не нужно: это паспорт единицы, а не фотоальбом.
const MAX_PHOTOS = 4;

// Подсветка распознанного. Не «галочка», а именно рамка вокруг поля: человек
// должен видеть, что это ввела модель, и перечитать — серийники она путает.
// ⚠️ Только цвет рамки, без прочих свойств: inline-стиль с `undefined`
// стирает шорткат border целиком.
const OCR_HL = { borderColor: 'var(--green, #0f9d58)' };


/**
 * Слот под один снимок. Внутри — общий UploadSlot из PhotosSection: там уже
 * отлажены выбор «камера / галерея / файлы», сторож пикера на Android и
 * синхронный input.click() для Safari. Своей копии не заводим.
 *
 * Вынесен наружу, а не объявлен в теле модалки: компонент, созданный внутри
 * другого компонента, пересоздаётся на каждом рендере и теряет состояние.
 */
function PhotoSlot({ label, hint, preview, busy, onFiles, onClear, onOpen, t }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {preview ? (
        // Квадрат через paddingTop:100% на пустом блоке + абсолютное фото внутри —
        // надёжнее голого aspect-ratio: не зависит от того, как родительский грид
        // resolve-ит ширину колонки. Тот же приём — ниже, для пустого слота и для
        // тайлов сетки в EquipmentPhotoGrid.
        <div style={{ position: 'relative', width: '100%', paddingTop: '100%', borderRadius: 10,
                      overflow: 'hidden', border: '1px solid var(--border)' }}>
          {/* Превью в слоте мелкое и обрезанное — разглядеть на нём серийник
              нельзя. Клик открывает общий просмотрщик с зумом, тот же, что
              у фото Работы. */}
          <img src={preview} alt="" onClick={onOpen}
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                        cursor: onOpen ? 'zoom-in' : 'default' }} />
          <button type="button" className="btn btn-sm" data-ph-capture="equipment_photo_clear"
                  style={{ position: 'absolute', right: 6, top: 6, padding: '2px 8px' }}
                  onClick={onClear} disabled={busy}>
            <Icon name="x" size={12} />
          </button>
        </div>
      ) : (
        <>
          <div style={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
            <UploadSlot onFiles={onFiles} uploading={busy} multiple={false} fill />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.3 }}>{hint}</div>
        </>
      )}
    </div>
  );
}

/**
 * Сетка до MAX_PHOTOS фото общего вида — механика та же, что у «Фото До/После»
 * в Работах (ReportPhotoGallery): тайл на кадр, крестик поверх для удаления,
 * клик открывает общий просмотрщик, плитка «Добавить» стоит последней ячейкой
 * и пропадает по достижении потолка.
 *
 * ⚠️ Без drag-and-drop: у До/После порядок кадров — часть смысла (что было
 * «до», что «после»), здесь же это просто общий вид с разных ракурсов, и
 * четыре штуки — редкий случай, где порядок вообще кто-то заметит. Решение
 * Павла 13.09.2026: тащить сюда dnd-kit и отдельную таблицу под 4 картинки —
 * цена, которую эта карточка не отбивает. Поменять порядок можно удалением и
 * повторной загрузкой.
 *
 * Вынесен наружу по той же причине, что и PhotoSlot/TimelineRow выше:
 * компонент, объявленный внутри другого, пересоздаётся на каждом рендере.
 */
function EquipmentPhotoGrid({ label, hint, photos, busy, onFiles, onRemove, onOpen }) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label} <span style={{ fontWeight: 400, color: 'var(--text3)' }}>{photos.length}/{MAX_PHOTOS}</span>
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        {photos.map((p, i) => (
          <div key={p.key || i} style={{ position: 'relative', width: '100%', paddingTop: '100%', borderRadius: 10,
                                          overflow: 'hidden', border: '1px solid var(--border)' }}>
            <img src={p.preview} alt="" onClick={() => onOpen(p)}
                 style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
            <button type="button" className="btn btn-sm" data-ph-capture="equipment_photo_remove"
                    style={{ position: 'absolute', right: 6, top: 6, padding: '2px 8px' }}
                    onClick={() => onRemove(i)} disabled={!!busy}>
              <Icon name="x" size={12} />
            </button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <div style={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
            <UploadSlot onFiles={onFiles} uploading={busy === 'photo'} multiple fill />
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.3 }}>{hint}</div>
    </div>
  );
}

// Тег статуса незавершённой Работы — «В работе»/«Ожидает». Отменённые
// (cancelled) в таймлайн оборудования вообще не попадают: они не история и
// не план, показывать тут нечего.
const STATUS_TAG = {
  in_progress: { bg: '#dbeafe', fg: '#1d4ed8' },
  pending:     { bg: '#fef3c7', fg: '#92400e' },
};

// Стиль маленькой иконочной кнопки для действий над наклейкой (печать,
// вывод из оборота) — тот же принцип, что и у карточки Контакта: рамка +
// нейтральный/опасный цвет, без текста, только title-подсказка.
function tagIconBtn(danger) {
  return {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${danger ? 'var(--red)' : 'var(--border)'}`,
    background: danger ? 'transparent' : 'var(--bg2)',
    color: danger ? 'var(--red)' : 'var(--text2)',
    cursor: 'pointer',
  };
}

/**
 * Одна строка «Запланировано» / «Истории обслуживания».
 *
 * Вынесен наружу, а не объявлен в теле модалки: компонент, созданный внутри
 * другого компонента, пересоздаётся на каждом рендере и теряет состояние.
 *
 * Слева — либо зелёная галочка (Работа выполнена), либо цветной тег статуса.
 * Раньше это было одним и тем же рядом без различий — а стоя перед печью
 * хочется понять с одного взгляда, чинили её уже или только собираются.
 */
function TimelineRow({ w, loc, onOpenTask, onClose, propertyId, showSite, t }) {
  const { i18n } = useTranslation();
  // Название Работы — на языке интерфейса, тем же правилом, что в списке Работ.
  const { displayTitle } = useTranslatedTask(w, (i18n.language || 'ru').slice(0, 2));
  const tag = STATUS_TAG[w.status];
  // Открытая экстренная Работа — это поломка, а не плановое ТО. Выделяем тем
  // же пульсирующим фоном `task-emergency`, что и в списке Работ и на Объекте,
  // плюс красная рамка и метка «Экстренная»: в «Запланировано» она не должна
  // сливаться с обычным обслуживанием.
  const urgent = w.type === 'emergency' && isActiveTask(w);
  return (
    <div className={`card clickable${urgent ? ' task-emergency' : ''}`} data-ph-capture="equipment_timeline_open"
         style={{ padding: 10, marginBottom: 6, cursor: onOpenTask ? 'pointer' : 'default',
                  ...(urgent ? { borderColor: 'rgba(220,38,38,0.5)', borderLeft: '3px solid #dc2626' } : null) }}
         // Вторым аргументом отдаём Объект: тому, кто открыл карточку с
         // дашборда, до Работы иначе не дойти — она живёт не на своём
         // маршруте, а внутри страницы Объекта.
         onClick={() => onOpenTask && (onOpenTask(w.id, propertyId), onClose())}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        {tag ? (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 6, flexShrink: 0,
            color: tag.fg, background: tag.bg,
          }}>
            {w.status === 'in_progress' ? t('tasks.statusInProgress') : t('tasks.statusPending')}
          </span>
        ) : (
          <Icon name="check" size={13} style={{ color: 'var(--green, #0f9d58)', flexShrink: 0 }} />
        )}
        <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>
          {w.completed_date || w.scheduled_date
            ? new Date((w.completed_date || w.scheduled_date) + 'T00:00:00')
                .toLocaleDateString(loc, { day: '2-digit', month: 'short', year: '2-digit' })
            : '—'}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>
          {w.type === 'emergency' && '🚨 '}{displayTitle}
        </span>
        {/* Метка — и у выполненной: в истории видно, какие работы были поломками. */}
        {w.type === 'emergency' && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 6, flexShrink: 0,
                         color: '#b91c1c', background: 'rgba(220,38,38,0.12)' }}>
            {t('tasks.typeEmergency')}
          </span>
        )}
      </div>
      {/* Переносная единица: история идёт по разным Объектам, и без подписи
          непонятно, где что делали. У прибитой к Объекту — все Работы на нём
          же, подпись была бы шумом. */}
      {showSite && w.property_name && w.property_id !== propertyId && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3, display: 'flex', gap: 4, alignItems: 'center' }}>
          <Icon name="pin" size={11} /> {w.property_name}
        </div>
      )}
      {w.assignees && (
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{w.assignees}</div>
      )}
      {/* Номер — вниз и мелким, как в обычной карточке Работы (низ: номер
          слева серым). Жирным первой строкой он только отвлекал от статуса,
          даты и названия — того, что реально нужно на объекте с одного взгляда. */}
      {w.display_number && (
        <div style={{ fontSize: 12, color: 'var(--text3)', fontVariantNumeric: 'tabular-nums', marginTop: 3 }}>
          {w.display_number}
        </div>
      )}
    </div>
  );
}

// Подписи типов перемещения — выводятся на сервере из концов маршрута
// (services/equipmentMobility.js movementKind), здесь только перевод.
const MOVE_KIND_KEY = {
  start: 'equipmentMove.kindStart',
  ship: 'equipmentMove.kindShip',
  return: 'equipmentMove.kindReturn',
  transfer: 'equipmentMove.kindTransfer',
};

function fmtMovedAt(sqlUtc, loc, tz) {
  const d = new Date(String(sqlUtc).replace(' ', 'T') + 'Z');
  const opts = { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' };
  try { return d.toLocaleString(loc, { ...opts, timeZone: tz || 'Asia/Dubai' }); }
  catch { return d.toLocaleString(loc, opts); }
}

function siteName(end, t) {
  if (!end) return '—';
  if (end.hidden) return t('equipmentMove.hiddenSite');
  return end.name || '—';
}

/**
 * Строка «Истории действий»: перемещение или приёмка. Вынесена наружу, а не
 * объявлена в теле секции (правило проекта: компоненты не внутри компонентов).
 */
function ActionRow({ item, loc, tz, t }) {
  const check = item.type === 'check';
  const who = check ? item.s.by_name : item.m.moved_by_name;
  const note = check ? item.s.note : item.m.note;
  return (
    <div className="card" style={{ padding: 10, marginBottom: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Icon name={check ? 'check' : 'truck'} size={13}
              style={{ color: check && item.s.reason === 'release_override' ? '#c77700' : check && item.s.to_status === 'ready' ? 'var(--green, #0f9d58)' : 'var(--text3)', flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>
          {/* Дата и время — по поясу организации, как их вводили. */}
          {fmtMovedAt(item.at, loc, tz)}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          {check
            ? t(item.s.reason === 'release_override' ? 'equipmentMove.actReleaseOverride'
                : item.s.to_status === 'ready' ? 'equipmentMove.actCheckPassed' : 'equipmentMove.actBackToCheck')
            : t(MOVE_KIND_KEY[item.m.kind] || MOVE_KIND_KEY.transfer)}
        </span>
      </div>
      {!check && (
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>
          {siteName(item.m.from, t)} → {siteName(item.m.to, t)}
        </div>
      )}
      {!check && item.m.rsa_number && (
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>
          {t('equipmentMove.rsa')}: <b>{item.m.rsa_number}</b>
        </div>
      )}
      {(who || note) && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
          {[who, note].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

/**
 * Блок «Перемещения» карточки. Приходит только у организаций с включённым
 * перемещением (или у единицы, которая уже ездила) — сервер не отдаёт
 * `mobility` остальным, и у них карточка не меняется ни на строку.
 *
 * Вынесен наружу, а не объявлен в теле модалки (см. TimelineRow).
 */
function MobilitySection({ mobility, loc, onMove, commands, t }) {
  const moves = mobility.movements || [];
  const here = moves[0]?.to || null;
  // «История действий» — перемещения и приёмка одной лентой, свежее сверху.
  // Приёмка — ручные смены статуса на складе (reason 'release' в журнале
  // статуса): «Приёмка пройдена» и «Возврат на приёмку». Задел под будущую
  // Работу-инспекцию с чек-листом приёмки: у записи журнала уже есть task_id —
  // когда приёмка пойдёт через Работу, строка сможет открывать её.
  const history = [
    ...moves.filter((m) => m.kind !== 'start').map((m) => ({ type: 'move', at: m.moved_at, key: `m${m.id}`, m })),
    ...(mobility.status_log || []).filter((s) => s.reason === 'release' || s.reason === 'release_override')
      .map((s) => ({ type: 'check', at: s.at, key: `s${s.id}`, s })),
  ].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  // История перемещений нужна не каждый раз — по умолчанию свёрнута.
  const [historyOpen, setHistoryOpen] = useState(false);
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                       letterSpacing: '.05em', flex: 1 }}>
          {t('equipmentMove.sectionTitle')}
        </span>
        {/* Кнопка «Переместить» — только у единицы вне цикла статусов
            (перемещение выключено, но единица уже ездила) — у остальных
            перемещения идут командами цикла ниже. */}
        {mobility.can_move && !(mobility.is_mobile && mobility.enabled) && (
          <button className="btn btn-sm btn-primary" data-ph-capture="equipment_move_open"
                  onClick={() => onMove()}>
            {t('equipmentMove.moveBtn')}
          </button>
        )}
      </div>

      {mobility.is_mobile && here && (
        <div style={{ fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span>{t('equipmentMove.now')}: <b>{siteName(here, t)}</b></span>
          {here.is_warehouse && (
            <span className="badge">{t('equipmentMove.warehouseBadge')}</span>
          )}
          <EquipmentStatusBadge status={mobility.status} />
        </div>
      )}

      {/* ── Статус (фаза 4) ──
          Экстренная Работа уже выделена и стоит первой в «Запланировано» —
          здесь не дублируем её, а говорим то, чего там нет: с какого момента
          идёт простой и сколько он длится. «Поломка» не ставит статус, а
          открывает обычную форму экстренной Работы с этой единицей. */}
      {mobility.is_mobile && mobility.status === 'breakdown' && mobility.downtime?.current && (
        <div style={{ fontSize: 12, color: '#b91c1c', marginTop: -4, marginBottom: 8 }}>
          {t('equipmentStatus.breakdownSince', {
            date: fmtMovedAt(mobility.downtime.current.since, loc, mobility.timezone),
            hours: mobility.downtime.current.hours,
          })}
        </div>
      )}

      {/* ── Цикл статусов и команды (вариант Б) ──
          Где единица в цикле и что с ней можно сделать — крупными кнопками под
          палец. «Другое перемещение» — полный диалог для редких маршрутов
          (склад → склад и т.п.). */}
      {mobility.is_mobile && mobility.enabled && (
        <EquipmentStatusFlow status={mobility.status} commands={commands} />
      )}
      {mobility.is_mobile && mobility.status === 'unknown' && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{t('equipmentStatus.unknownHint')}</div>
      )}
      {mobility.is_mobile && mobility.status === 'in_service' && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{t('equipmentStatus.inServiceHint')}</div>
      )}
      {/* Итог за всё время — когда он что-то добавляет к текущей строке:
          единственная поломка, которая идёт сейчас, уже показана выше. */}
      {mobility.is_mobile && mobility.downtime?.count > 0
        && !(mobility.downtime.count === 1 && mobility.downtime.current) && (
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
          {t('equipmentStatus.downtimeTotal', { hours: mobility.downtime.total_hours, count: mobility.downtime.count })}
        </div>
      )}

      {/* Подтверждённость — условие перемещения; без подсказки кнопки просто
          нет, и человек не понимает почему. */}
      {mobility.is_mobile && !mobility.can_move && mobility.enabled && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
          {t('equipmentMove.cannotMoveHint')}
        </div>
      )}

      {/* ── История действий — аккордеон (стиль — как «Служебные» в фото
          Работы). Редкие маршруты, которых нет среди команд (склад → склад),
          делаются массовым перемещением на странице «Инвентаризация». ── */}
      {history.length > 0 && (
        <div data-ph-capture="equipment_actions_history_toggle"
             onClick={() => { track('equipment_actions_history_toggled', { open: !historyOpen, count: history.length }); setHistoryOpen((v) => !v); }}
             style={{
               display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
               cursor: 'pointer', userSelect: 'none', marginTop: 4, marginBottom: 6,
               background: historyOpen ? 'rgba(79,142,247,0.12)' : 'var(--bg2)',
               border: '1px solid', borderColor: historyOpen ? 'var(--accent)' : 'var(--border)',
             }}>
          <Icon name="truck" size={14} style={{ color: 'var(--text3)' }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', flex: 1 }}>{t('equipmentMove.historyTitle')}</span>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{history.length}</span>
          <Icon name="chevronRight" size={14}
                style={{ color: 'var(--text3)', transform: historyOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        </div>
      )}
      {historyOpen && history.map((h) => (
        <ActionRow key={h.key} item={h} loc={loc} tz={mobility.timezone} t={t} />
      ))}
    </div>
  );
}

/**
 * «Переместить сюда» — плашка карточки, открытой по наклейке (фаза 2).
 *
 * Сервер отдаёт mobility.scan, только когда это имеет смысл: единица
 * переносная и подтверждённая, у человека есть право, а сегодня у него есть
 * Работа на другом Объекте. Один Объект — одна кнопка; несколько — выбор, не
 * угадываем: молча подставленный не тот Объект хуже отсутствия подсказки.
 *
 * Текущий Объект может прийти без названия — единица стояла у другого
 * клиента, наклейка разрешает её забрать, но не узнать, у кого она была.
 *
 * Вынесен наружу, а не объявлен в теле модалки (см. TimelineRow).
 */
function ScanMoveBanner({ scan, busy, onMove, t }) {
  const [pick, setPick] = useState(scan.targets.length === 1 ? String(scan.targets[0].id) : '');
  const cur = scan.current?.name
    ? scan.current.name
    : t('equipmentMove.scanElsewhere');
  const single = scan.targets.length === 1;
  return (
    <div style={{
      padding: '10px 14px', marginBottom: 14, borderRadius: 10,
      background: 'rgba(37,99,235,0.08)', border: '1px solid var(--accent, #2563eb)',
      fontSize: 13,
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
        <Icon name="truck" size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          {t('equipmentMove.scanListed', { place: cur })}
          {single && <> {t('equipmentMove.scanToday', { place: scan.targets[0].name })}</>}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!single && (
          <select className="form-input" value={pick} disabled={busy}
                  data-ph-capture="equipment_scan_move_pick"
                  onChange={(e) => setPick(e.target.value)}
                  style={{ flex: 1, minWidth: 160 }}>
            <option value="">{t('equipmentMove.scanPick')}</option>
            {scan.targets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <button className="btn btn-sm btn-primary" disabled={busy || !pick}
                data-ph-capture="equipment_scan_move"
                onClick={() => onMove(Number(pick))}>
          <BusyLabel busy={busy} label={t('common.saving')}>{t('equipmentMove.scanMoveHere')}</BusyLabel>
        </button>
      </div>
    </div>
  );
}

/**
 * onChanged(id) — единица изменилась, а карточка остаётся открытой: переезд,
 * смена статуса, новая/закрытая Работа («Поломка» — экстренная Работа меняет
 * статус на «Неисправна»). Родитель перечитывает свой список (Объект, «Парк»).
 */
export default function EquipmentModal({ equipmentId, propertyId, tagToken, scanToken, startEditing, onClose, onSaved, onOpenTask, onChanged }) {
  const { t, i18n } = useTranslation();
  const { can } = usePerm();
  const loc = i18n.language === 'en' ? 'en-US' : 'ru-RU';
  const isNew = !equipmentId;
  // Справочник организации (Настройки → «Типы оборудования»); поле type
  // необязательное — пустой выбор допустим, тип может не потребоваться.
  const { types: equipmentTypes } = useEquipmentTypes();

  const [row, setRow] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(isNew || !!startEditing);

  // Два разных снимка, и путать их нельзя.
  //
  //   photos — общий вид: по нему человек на объекте узнаёт нужную единицу
  //            среди четырёх одинаковых холодильников. До 4 штук — с разных
  //            ракурсов. В публичном паспорте фото НЕТ (см. equipment_implementation_plan.md).
  //   plate  — шильдик: табличка с моделью и серийником. Нужен ровно для
  //            распознавания и почти нечитаем как иллюстрация, поэтому один.
  //
  // Раньше «фото» было одним полем на оба смысла, и подпись «Фотография
  // оборудования» спорила с подсказкой «снимите шильдик» — пользователь
  // выбирал между двумя разными задачами, а получал одну картинку, плохо
  // решающую обе. Теперь общий вид ещё и множится: один ракурс не всегда
  // узнаваем в ряду одинаковых холодильников.
  //
  // Каждый элемент photos: { key, thumbKey, preview, full }. key/thumbKey —
  // R2-ключи (уходят на сервер), preview/full — локальные blob-URL для показа
  // в слоте и в просмотрщике соответственно.
  const [photos, setPhotos] = useState([]);
  const [plateKey, setPlateKey] = useState(null);
  const [platePreview, setPlatePreview] = useState(null);
  const [uploading, setUploading] = useState(null); // null | 'photo' | 'plate'
  const [ocrState, setOcrState] = useState('idle'); // idle | running | done | empty | failed | limit
  const [ocrFields, setOcrFields] = useState(() => new Set());
  // Что показать в полноэкранном просмотре. Ссылка ПОЛНОГО кадра, не превью:
  // превью 400px как раз и не позволяет прочитать то, ради чего его открывают.
  const [viewerSrc, setViewerSrc] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);
  // Предзаполнение формы Работы: { type: 'emergency' } — «Поломка» (фаза 4).
  const [taskPrefill, setTaskPrefill] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  // Гейт «Готова»: сервер ответил CHECK_REQUIRED — показываем причину и (Админу) выпуск без QA.
  const [gate, setGate] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [moving, setMoving] = useState(false);
  const [scanMoving, setScanMoving] = useState(false);
  const sheet = useTagSheet();

  // ── Наклейка, отсканированная прямо из этой карточки ──
  //
  //   localTagToken — код, который привяжется ПРИ СОХРАНЕНИИ. Используется
  //                   только для новой единицы: у существующей код и так
  //                   привязывается сразу же (см. onTagResolved).
  //   scanning      — открыт ли сканер поверх карточки.
  //   scanBusy      — идёт запрос привязки к УЖЕ существующей единице; кнопка
  //                   и форма блокируются на это время отдельно от saving.
  const [localTagToken, setLocalTagToken] = useState(tagToken || null);
  const [scanning, setScanning] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  // Похожие единицы, найденные сервером при создании. ⚠️ Показываем ПОСЛЕ
  // сохранения, а не вместо него: человек уже сфотографировал шильдик и
  // заполнил форму, отнимать у него результат ради подозрения нельзя. Экран
  // неблокирующий — «открыть существующую» или «это другая».
  const [dupes, setDupes] = useState(null);
  const [createdId, setCreatedId] = useState(null);

  const canOcr = can('equipment.ai_assist');
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Тронул поле руками — метка «распознано» с него снимается: дальше это
    // его ответственность, а не модели.
    setOcrFields((prev) => { const n = new Set(prev); n.delete(k); return n; });
  };

  /**
   * ⚠️ iOS Safari (нативный календарь `type="date"`): кнопка «Reset» в шторке
   * очищает DOM-значение поля, но не всегда шлёт `input`/`change` — контролируемый
   * инпут при следующем рендере возвращает своё СТАРОЕ значение из state, и
   * дата визуально «не сбрасывается», хотя браузер её уже сбросил. Найдено на
   * DEV 13.09.2026 (поля «Установлено»/«Гарантия до» в Новом оборудовании).
   *
   * onBlur — гарантированная подстраховка поверх onChange, а не замена: шторка
   * закрывается — блюр случается всегда, чем бы её ни закрыли (Reset, ✓, тап
   * мимо), и на этот момент `e.target.value` уже содержит то, что реально
   * лежит в DOM, даже если событие изменения само не долетело.
   */
  const syncDate = (k) => (e) => { if (e.target.value !== form[k]) set(k, e.target.value); };

  const load = useCallback(() => {
    // Сбрасываем ошибку на входе: иначе сообщение от прошлой попытки висит
    // поверх успешно загруженной карточки и выглядит как отказ на пустом месте.
    setError(null);
    if (isNew) { setRow({ can_edit: true, timeline: [] }); return; }
    // Код с наклейки идёт вместе с запросом: без него карточка Объекта вне
    // области данных отдаёт 404, и скан подменяющего мастера упирается в
    // «не найдено» перед печью, которую он держит в руках.
    api.equipmentGet(equipmentId, scanToken)
      .then((d) => {
        setRow(d);
        setPhotos((Array.isArray(d.photos) ? d.photos : []).slice(0, MAX_PHOTOS).map((p) => ({
          key: p.r2_key || null,
          thumbKey: p.thumb_r2_key || null,
          preview: p.thumb_url || p.url || null,
          full: p.url || null,
        })));
        setPlateKey(d.plate_r2_key || null);
        setPlatePreview(d.plate_url || null);
        setForm({
          name: d.name || '', type: d.type || '', manufacturer: d.manufacturer || '', model: d.model || '',
          serial_no: d.serial_no || '', installed_at: d.installed_at || '',
          warranty_until: d.warranty_until || '', notes: d.notes || '',
          is_mobile: !!d.is_mobile,
        });
      })
      // Причину показываем прямо в тексте: «не удалось» без деталей превращает
      // любой отказ в загадку и для нас, и для тенанта.
      .catch((e) => setError(`${t('equipment.loadFailed')}${e?.message ? ` (${e.message})` : ''}`));
  }, [equipmentId, isNew, scanToken, t]);

  useEffect(() => { load(); }, [load]);

  // Escape закрывает — на keyup, а не keydown: иначе всплывший из подтверждения
  // keyup закрывает и родителя тоже.
  //
  // ⚠️ Пока открыт просмотрщик ИЛИ форма новой Работы — не закрываемся. Оба
  // слушают Escape сами (просмотрщик — keyup, TaskModal — свой guardedClose
  // на keyup), и без этой проверки одно нажатие схлопывает оба слоя разом:
  // человек хотел выйти из вложенной формы, а терял и карточку оборудования
  // под ней. Тот же приём, что в RequestDetailModal (`if (showTask) return`).
  // ⚠️ Сканер — тоже полноэкранный слой поверх карточки, добавлен в ту же
  // проверку: иначе Escape внутри сканера (у него своего слушателя нет, только
  // кнопка закрытия) закрывал бы карточку под ним.
  //
  // Правка открытой карточки («Редактировать») — тоже вложенный слой: Escape
  // возвращает к карточке, как кнопка «Отмена», а не закрывает всё окно.
  // Окно, открытое сразу в правке (новое оборудование / startEditing), как и
  // «Отмена», закрывается целиком. Пока идёт сохранение — Escape игнорируем.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || viewerSrc || creatingTask || scanning || moving) return;
      if (editing && !isNew && !startEditing) { if (!saving) setEditing(false); return; }
      onClose();
    };
    window.addEventListener('keyup', onKey);
    return () => window.removeEventListener('keyup', onKey);
  }, [onClose, viewerSrc, creatingTask, scanning, moving, editing, isNew, startEditing, saving]);

  /** Загружает один blob в R2 и возвращает ключ. */
  const put = useCallback(async (blob, kind) => {
    const contentType = blob.type || 'image/jpeg';
    const { uploadUrl, key } = await api.equipmentPresign(contentType, kind);
    const r = await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': contentType } });
    if (!r.ok) throw new Error(`R2 ${r.status}`);
    return key;
  }, []);

  // Шильдик — как раньше, один слот, UploadSlot отдаёт массив (он рассчитан
  // на мультизагрузку), берём первый файл.
  const onPlateFiles = async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;

    setUploading('plate');
    setError(null);
    setOcrState('idle');
    try {
      const blob = await compressImage(file, PLATE_MAX, PLATE_Q);
      setPlateKey(await put(blob, 'plate'));
      setPlatePreview(URL.createObjectURL(blob));
      track('equipment_photo_uploaded', { kind: 'plate', bytes: blob.size });
    } catch (err) {
      setError(`${t('equipment.photoFailed')}${err?.message ? ` (${err.message})` : ''}`);
    } finally {
      setUploading(null);
    }
  };

  /**
   * Общий вид — до MAX_PHOTOS штук. UploadSlot теперь отдаёт НЕСКОЛЬКО файлов
   * разом (мультивыбор в галерее); лишние сверх остатка мест отбрасываем
   * молча — отказ ради двух-трёх кадров хуже, чем взять первые и не спорить.
   *
   * Кадры добавляются в состояние ПО ОДНОМУ по мере загрузки, а не все разом
   * в конце: если пятый в очереди оборвётся на объектовом интернете, первые
   * четыре не должны пропасть вместе с ним.
   */
  const onPhotoFiles = async (files) => {
    const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
    if (!list.length) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) return;

    setUploading('photo');
    setError(null);
    try {
      for (const file of list.slice(0, remaining)) {
        // Полный кадр и превью грузим параллельно: последовательно это две
        // задержки сети подряд, а на объекте связь и так тонкая.
        const [full, thumb] = await Promise.all([
          compressImage(file, PHOTO_MAX, PHOTO_Q),
          compressImage(file, THUMB_MAX, THUMB_Q),
        ]);
        const [fullKey, tKey] = await Promise.all([put(full, 'photo'), put(thumb, 'photo_thumb')]);
        setPhotos((prev) => [...prev, {
          key: fullKey, thumbKey: tKey,
          preview: URL.createObjectURL(thumb),   // в тайле показываем лёгкое
          full: URL.createObjectURL(full),       // в просмотре — полный кадр
        }]);
        track('equipment_photo_uploaded', { kind: 'photo', bytes: full.size, thumb_bytes: thumb.size });
      }
    } catch (err) {
      // Часть кадров могла успеть загрузиться до сбоя — они уже в photos и
      // никуда не денутся, ошибка касается только оставшихся в очереди.
      setError(`${t('equipment.photoFailed')}${err?.message ? ` (${err.message})` : ''}`);
    } finally {
      setUploading(null);
    }
  };

  const clearPlate = () => { setPlateKey(null); setPlatePreview(null); setOcrState('idle'); };
  const removePhoto = (idx) => { setPhotos((prev) => prev.filter((_, i) => i !== idx)); };

  // Распознавание — только по кнопке. Автозапуск при загрузке фото выглядел бы
  // приятнее, но каждый кадр — платный вызов модели, а промахнувшийся палец
  // или повторный выбор файла превращаются в счёт, которого никто не заметил.
  const recognize = async () => {
    if (!plateKey) return;
    setOcrState('running');
    setError(null);
    try {
      const res = await api.equipmentRecognizePlate(plateKey);
      track('equipment_plate_ocr', { recognized: !!res.recognized, confidence: res.confidence });

      if (!res.recognized) { setOcrState('empty'); return; }

      const filled = new Set();
      setForm((f) => {
        const next = { ...f };
        for (const [k, v] of Object.entries(res.fields || {})) {
          // Не затираем то, что человек уже ввёл руками: его данные надёжнее.
          if (v && !next[k]) { next[k] = v; filled.add(k); }
        }
        return next;
      });
      setOcrFields(filled);
      setOcrState(filled.size ? 'done' : 'empty');
    } catch (e) {
      setOcrState(e?.code === 'OCR_DAILY_LIMIT' ? 'limit' : 'failed');
    }
  };

  const save = async () => {
    if (!form.name.trim()) { setError(t('equipment.nameRequired')); return; }
    setSaving(true);
    setError(null);
    let newId = null;
    try {
      if (isNew) {
        const res = await api.equipmentCreate({
          ...form, property_id: propertyId,
          photos: photos.map((p) => ({ r2_key: p.key, thumb_r2_key: p.thumbKey })),
          plate_r2_key: plateKey,
          plate_ocr_used: ocrState === 'done',
          // Код мог прийти снаружи (сканер вне карточки, EquipmentScanFlow)
          // или изнутри — кнопкой «Сканировать QR» прямо в этой форме.
          tag_token: localTagToken || undefined,
        });
        newId = res.id;
        track('equipment_created', {
          confirmed: res.confirmed,
          duplicates: (res.duplicates || []).length,
          photo_count: photos.length,
          with_plate: !!plateKey,
          ocr_used: ocrState === 'done',
        });

        // Сервер нашёл похожее на этом же Объекте — показываем, не закрываясь.
        // Дубль дешевле не создать, чем потом склеивать разошедшуюся историю,
        // а человек прямо сейчас стоит перед железом и может сверить глазами.
        if ((res.duplicates || []).length) {
          setDupes(res.duplicates);
          setCreatedId(res.id);
          setSaving(false);
          track('equipment_duplicate_shown', { count: res.duplicates.length });
          return;
        }
      } else {
        // Пометка учёта перемещений — только тем, кому её можно менять (сервер
        // и так проигнорирует без тумблера организации, но не шлём зря).
        const { is_mobile: isMobile, ...fields } = form;
        const canToggleMobile = !!row?.mobility?.can_toggle_mobile;
        await api.equipmentUpdate(equipmentId, {
          ...fields,
          ...(canToggleMobile ? { is_mobile: isMobile } : {}),
          photos: photos.map((p) => ({ r2_key: p.key, thumb_r2_key: p.thumbKey })),
          plate_r2_key: plateKey,
        });
        if (canToggleMobile && isMobile !== !!row.mobility.is_mobile) {
          track('equipment_mobile_toggled', { enabled: isMobile });
        }
        track('equipment_updated', { equipment_id: equipmentId });
      }
      onSaved(newId);
    } catch (e) {
      setError(e?.message || t('equipment.saveFailed'));
      setSaving(false);
    }
  };

  const confirm = async () => {
    await api.equipmentConfirm(equipmentId);
    track('equipment_confirm', { bulk: false, count: 1 });
    onSaved();
  };

  /**
   * Выпуск в работу (фаза 4): «Приёмка пройдена → Готова» или вернуть «На
   * приёмку». Флаг equipment.release; сервер проверяет, что единица на
   * Складе и по ней нет открытых Работ.
   */
  const releaseStatus = async (to, overrideNote) => {
    if (statusBusy) return;
    setStatusBusy(true);
    setError(null);
    try {
      await api.equipmentSetStatus(equipmentId, to, undefined, overrideNote);
      track('equipment_status_released', { to, override: !!overrideNote });
      setGate(null); setOverrideReason('');
      onChanged && onChanged(equipmentId);
      load();
    } catch (err) {
      if (err.code === 'CHECK_REQUIRED') {
        track('equipment_release_blocked', { reason: err.body?.gate?.reason, can_override: !!err.body?.can_override });
        setGate({ ...(err.body?.gate || {}), can_override: !!err.body?.can_override });
        return;
      }
      setError(err.code === 'OPEN_SERVICE_TASKS' ? t('equipmentStatus.errOpenTasks')
        : err.code === 'NOT_ON_WAREHOUSE' ? t('equipmentStatus.errNotOnWarehouse')
        : (err.message || t('equipmentMove.errGeneric')));
    } finally {
      setStatusBusy(false);
    }
  };

  /**
   * «Переместить сюда» по наклейке. Дату не шлём — сервер ставит «сейчас»:
   * человек стоит перед железом, это и есть момент перемещения. Код наклейки
   * уходит вместе с запросом — он разрешает забрать единицу с Объекта вне
   * области данных (вариант А, equipment_mobility_plan.md).
   */
  const scanMove = async (toId) => {
    if (scanMoving || !toId) return;
    setScanMoving(true);
    setError(null);
    const scan = row?.mobility?.scan;
    try {
      const body = { to_property_id: toId, scan_token: scanToken };
      const send = async () => {
        try {
          await api.equipmentMove(equipmentId, body);
        } catch (err) {
          // Неготовая единица (фаза 4): спрашиваем тем же системным confirm, что
          // и вывод наклейки из оборота, и повторяем с подтверждением.
          if (err.code === 'UNREADY_CONFIRM') {
            const st = err.body?.unit_status;
            if (!window.confirm(t('equipmentStatus.confirmUnready', { status: st ? t(`equipmentStatus.${st}`) : '—' }))) return false;
            body.confirm_unready = true;
            track('equipment_dispatch_unready_confirmed', { source: 'scan' });
            return send();
          }
          // Тенант требует номер RSA на отгрузке: спрашиваем системным prompt,
          // как и подтверждения выше (скан — быстрый путь без диалога).
          if (err.code === 'RSA_REQUIRED') {
            const v = window.prompt(t('equipmentMove.rsaPrompt'));
            if (!v || !v.trim()) return false;
            body.rsa_number = v.trim();
            track('equipment_move_rsa_prompted', { source: 'scan' });
            return send();
          }
          throw err;
        }
        return true;
      };
      if (!(await send())) return;
      track('equipment_moved', {
        source: 'scan', count: 1,
        kind: scan?.current?.is_warehouse ? 'ship' : 'transfer',
        out_of_scope: row?.in_scope === false,
        choices: scan?.targets?.length || 0,
      });
      onChanged && onChanged(equipmentId);
      load();
    } catch (err) {
      track('equipment_move_failed', { code: err.code || 'unknown', source: 'scan' });
      setError(MOVE_ERROR_KEYS[err.code] ? t(MOVE_ERROR_KEYS[err.code]) : (err.message || t('equipmentMove.errGeneric')));
    } finally {
      setScanMoving(false);
    }
  };

  /**
   * Пометить единицу переносной / снять пометку. Сразу по клику, без
   * «Сохранить»: это не поле паспорта, а режим учёта — после него у карточки
   * появляется (или пропадает) «Переместить». Первая пометка пишет на сервере
   * стартовую строку журнала.
   */
  /**
   * Вывести наклейку из оборота. ⚠️ Необратимо и выключает физический стикер,
   * который уже висит на железе, поэтому спрашиваем, называя код: строки в
   * списке выглядят одинаково, и промах пальцем стоит выезда с переклейкой.
   *
   * При обычной перепечатке это НЕ нужно — на затёртый стикер клеится второй,
   * а старый живёт, пока читается. Только для «код утёк / наклейка уехала
   * вместе со снятой панелью».
   */
  const revokeTag = async (tag) => {
    if (!window.confirm(t('equipmentTags.revokeConfirm', { code: formatCode(tag.token) }))) return;
    setRevoking(tag.id);
    try {
      await api.equipmentRevokeTag(tag.token);
      invalidateHasTags();
      track('equipment_tag_revoked', { was_bound: true, source: 'card' });
      load();
    } catch (e) {
      setError(e?.message || t('equipmentTags.revokeFailed'));
    } finally {
      setRevoking(null);
    }
  };

  /**
   * Разбор скана из кнопки «Сканировать QR» прямо в этой карточке.
   *
   * ⚠️ Это НЕ полный разбор EquipmentScanFlow — тот решает «на каком Объекте»
   * и «к какой единице привязать», а тут Объект и единица уже известны (это
   * ОНА, форма перед глазами). Исходов поэтому не четыре, а два:
   *
   *   код свободен  → у новой единицы нет id, привязка уйдёт вместе с
   *                    сохранением (localTagToken, тот же путь, что и код,
   *                    пришедший снаружи через tagToken); у существующей —
   *                    привязываем сразу же, не дожидаясь «Сохранить».
   *   код уже занят → своей же единицы — не ошибка, наклейка и так на месте;
   *                    чужой — тот же текст, что и у гонки двух воркеров
   *                    (equipment.tagTaken), другая наклейка.
   */
  const onTagResolved = async (res, token) => {
    setScanning(false);
    if (res.state === 'free') {
      if (isNew) {
        setLocalTagToken(token);
        track('equipment_scan', { source: 'card_new', state: 'free' });
        return;
      }
      setScanBusy(true);
      setError(null);
      try {
        await api.equipmentBindTag(token, equipmentId);
        track('equipment_tag_bind', { created_new: false, source: 'card' });
        load();
      } catch (e) {
        setError(e?.code === 'TAG_TAKEN' ? t('equipment.tagTaken') : (e?.message || t('equipment.bindFailed')));
      } finally {
        setScanBusy(false);
      }
      return;
    }

    // state === 'bound'
    if (!isNew && String(res.equipment_id) === String(equipmentId)) {
      track('equipment_scan', { source: 'card', state: 'bound_self' });
      return;
    }
    track('equipment_scan', { source: 'card', state: 'bound_other' });
    setError(t('equipment.tagTaken'));
  };

  // ⚠️ Модалку рендерим ВСЕГДА, даже пока карточка не загрузилась и даже если
  // загрузка провалилась. Раньше здесь стоял `if (!row) return null` — и любой
  // сбой запроса выглядел как «клик по плитке ничего не делает»: ни окна, ни
  // ошибки, ни намёка, куда смотреть. Текст ошибки уже лежал в состоянии, но
  // показать его было негде.
  const loading = !row && !error;
  const draft = !isNew && row && !row.confirmed_at;
  const canEdit = !!row?.can_edit;
  const timeline = row?.timeline || [];
  // Одна выборка с сервера, два тайла на фронте: статус уже приходит в
  // каждой строке. Отменённые (t.status === 'cancelled') не попадают ни
  // туда, ни туда — это не история и не план.
  const plannedTimeline = timeline.filter((w) => w.status === 'pending' || w.status === 'in_progress');
  const completedTimeline = timeline.filter((w) => w.status === 'done');

  // ── Команды цикла статусов (EquipmentStatusFlow, вариант Б) ──
  // Каждая ведёт в уже существующий механизм: диалог перемещения с суженным
  // списком, форму Работы, POST /status, открытие Работы. Права — те же, что
  // у этих механизмов: нет права — нет кнопки (а не 403 по клику).
  const mob = row?.mobility;
  const flowOn = !!row && !isNew && row.in_scope !== false && !!mob?.is_mobile && !!mob?.enabled;
  const canTask = can('tasks.create');
  const openTask = (id) => { if (onOpenTask && id) { onOpenTask(id, row.property_id); onClose(); } };
  const breakdownTask = mob?.open_emergency?.[0]?.id || null;
  const serviceTask = plannedTimeline.find((w) => w.type !== 'emergency' && w.property_id === row?.property_id)?.id || null;
  const newTask = (prefill) => {
    track(prefill?.type === 'emergency' ? 'equipment_breakdown_report_open' : 'equipment_repair_open');
    setTaskPrefill(prefill); setCreatingTask(true);
  };
  const flowCommands = !flowOn ? [] : ({
    ready: [
      mob.can_move && { key: 'ship', to: 'on_hire', primary: true, label: t('equipmentStatus.cmd.ship'), onClick: () => setMoving('ship') },
      mob.can_release && { key: 'back_to_check', to: 'off_hire', label: t('equipmentStatus.backToCheck'), busy: statusBusy, onClick: () => releaseStatus('off_hire') },
    ],
    on_hire: [
      mob.can_move && { key: 'return', to: 'off_hire', primary: true, label: t('equipmentStatus.cmd.return'), onClick: () => setMoving('return') },
      canTask && { key: 'breakdown', danger: true, label: t('equipmentStatus.cmd.breakdown'), onClick: () => newTask({ type: 'emergency' }) },
      mob.can_move && { key: 'transfer', label: t('equipmentStatus.cmd.transfer'), onClick: () => setMoving('transfer') },
    ],
    breakdown: [
      onOpenTask && breakdownTask && { key: 'fixed', to: 'on_hire', primary: true, label: t('equipmentStatus.cmd.fixed'), onClick: () => openTask(breakdownTask) },
      mob.can_move && { key: 'return_swap', label: t('equipmentStatus.cmd.returnSwap'), onClick: () => setMoving('return') },
    ],
    off_hire: [
      mob.can_release && { key: 'release', to: 'ready', primary: true, label: t('equipmentStatus.releaseReady'), busy: statusBusy, onClick: () => releaseStatus('ready') },
      canTask && { key: 'repair', label: t('equipmentStatus.cmd.repair'), onClick: () => newTask(null) },
    ],
    // «Неизвестно» — существующий парк до первой инспекции: осмотр запускается в блоке «Осмотры»,
    // дальше человек сам ставит «Готова» (за гейтом QA) или «На приёмку».
    unknown: [
      mob.can_release && { key: 'release', to: 'ready', primary: true, label: t('equipmentStatus.releaseReady'), busy: statusBusy, onClick: () => releaseStatus('ready') },
      mob.can_release && { key: 'back_to_check', to: 'off_hire', label: t('equipmentStatus.backToCheck'), busy: statusBusy, onClick: () => releaseStatus('off_hire') },
    ],
    in_service: [
      onOpenTask && serviceTask && { key: 'open_service', to: 'off_hire', primary: true, label: t('equipmentStatus.cmd.openService'), onClick: () => openTask(serviceTask) },
    ],
  }[mob.status] || []).filter(Boolean);

  // Перевод для текущего языка интерфейса. Канон живёт в основной таблице, а
  // здесь лежит машинный перевод на остальные языки — поэтому при русском
  // интерфейсе строки просто нет, и мы честно показываем оригинал.
  //
  // ⚠️ Только для ЧТЕНИЯ. В форме редактирования всегда оригинал: иначе
  // англичанин сохранит машинный перевод поверх того, что ввёл автор, и канон
  // потеряется навсегда.
  const uiLang = i18n.language === 'en' ? 'en' : 'ru';
  const tr = row?.translations?.[uiLang] || null;
  const shownName = (!editing && tr?.name) || row?.name;
  const shownNotes = (!editing && tr?.notes) || form.notes;

  /**
   * Похожее уже есть на этом Объекте.
   *
   * Отдельный экран ПОСЛЕ сохранения, а не проверка вместо него: запись уже
   * создана, ничего не потеряно, человек просто решает — дубль или нет. Он
   * стоит перед железом и может сверить глазами, а прораб потом будет решать
   * то же самое по названиям в списке.
   *
   * Склеить воркер не может (merge — право подтверждающего), но может открыть
   * существующую и не заводить третью «печку большую». Дубль дешевле не
   * создать, чем потом сращивать разошедшуюся историю.
   *
   * ⚠️ Ранний return безопасен: все хуки объявлены выше.
   */
  if (dupes) {
    return (
      <div className="modal-overlay" {...backdropClose(() => onSaved(createdId))}>
        <div className="modal" style={{ maxWidth: 460 }}>
          <div className="modal-header">
            <h3 style={{ margin: 0 }}>{t('equipment.dupTitle')}</h3>
            <button className="modal-close" onClick={() => onSaved(createdId)}
                    aria-label={t('common.close')}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>
              {t('equipment.dupHint')}
            </div>
            {dupes.map((d) => (
              <div key={d.id} className="card clickable" style={{ padding: 10, marginBottom: 6, cursor: 'pointer' }}
                   data-ph-capture="equipment_duplicate_open"
                   onClick={() => { track('equipment_duplicate_opened'); onSaved(d.id); }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</div>
                {(d.model || d.serial_no) && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                    {[d.model, d.serial_no].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" data-ph-capture="equipment_duplicate_dismiss"
                    onClick={() => { track('equipment_duplicate_dismissed'); onSaved(createdId); }}>
              {t('equipment.dupDifferent')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Закрытие по фону — через общий backdropClose: он смотрит на
    // pointerdown-таргет, поэтому клик, начатый внутри модалки и отпущенный на
    // фоне, её не закрывает. Своей копии этой логики не заводим.
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="tool" size={18} />
            {isNew ? t('equipment.addTitle') : (shownName || t('equipment.title'))}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
              {t('common.loading')}
            </div>
          )}

          {/* Не загрузилось — показываем причину и кнопку повтора, а не пустоту.
              Форму при этом не рисуем: править нечего. */}
          {!row && error && (
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>
              <button className="btn btn-sm" onClick={load}>{t('common.retry')}</button>
            </div>
          )}

          {/* Пришли по наклейке на чужой Объект. Не отказ, а объяснение, почему
              карточка не редактируется: иначе человек ищет кнопку правки и
              решает, что приложение сломалось. */}
          {row && row.in_scope === false && (
            <div style={{
              padding: '10px 14px', marginBottom: 14, borderRadius: 10,
              background: 'var(--bg3, var(--bg2))', border: '1px solid var(--border)',
              fontSize: 13, color: 'var(--text2)', display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <Icon name="alert" size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{t('equipment.outOfScope')}</span>
            </div>
          )}

          {row && !isNew && row.mobility?.scan && (
            <ScanMoveBanner scan={row.mobility.scan} busy={scanMoving} onMove={scanMove} t={t} />
          )}

          {row && draft && (
            <div style={{
              padding: '10px 14px', marginBottom: 14, borderRadius: 10,
              background: 'rgba(217,119,6,0.10)', border: '1px solid var(--orange)',
              fontSize: 13, color: 'var(--text)',
            }}>
              {t('equipment.draftHint')}
              {row.can_confirm !== false && can('equipment.confirm') && (
                <button className="btn btn-sm btn-primary" style={{ marginLeft: 10 }}
                        data-ph-capture="equipment_confirm_single" onClick={confirm}>
                  {t('equipment.confirm')}
                </button>
              )}
            </div>
          )}

          {/* ── Осмотры по чек-листам: запуск и история (equipment_checks_plan.md) ── */}
          {row && !isNew && !editing && row.confirmed_at && (
            <EquipmentChecks equipmentId={row.id} propertyId={row.property_id}
              canRun={!!row.can_run_checks} onOpenTask={onOpenTask} onClose={onClose} />
          )}

          {/* ── Запланировано — над Историей: сперва то, что ещё предстоит ──
              Запланировано, История и Перемещения — только в режиме просмотра:
              «Редактировать» правит саму карточку оборудования, а не его
              жизнь, и длинная лента над полями уводила форму за экран. */}
          {row && !isNew && !editing && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                               letterSpacing: '.05em', flex: 1 }}>
                  {t('equipment.scheduled')}
                </span>
                {/* ⚠️ Замыкает основной сценарий фичи. Без этой кнопки человек
                    стоит перед сломанной печью, читает историю — и упирается в
                    тупик: чтобы завести выезд, надо выйти, найти Объект,
                    создать Работу и вспомнить, какое оборудование к ней
                    привязать. Ровно тот путь, от которого сканирование уводило.

                    Вне области данных не показываем: чтение по наклейке даёт
                    историю, но не право распоряжаться чужим Объектом. */}
                {row.in_scope !== false && can('tasks.create') && (
                  <button className="btn btn-sm btn-primary" data-ph-capture="equipment_task_create"
                          onClick={() => {
                            track('equipment_task_create_from_card', { has_history: timeline.length > 0 });
                            setTaskPrefill(null);
                            setCreatingTask(true);
                          }}>
                    {t('equipment.createTask')}
                  </button>
                )}
              </div>
              {plannedTimeline.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>
                  {t('equipment.noScheduled')}
                </div>
              ) : plannedTimeline.map((w) => (
                <TimelineRow key={w.id} w={w} loc={loc} onOpenTask={onOpenTask} onClose={onClose}
                             propertyId={row.property_id} showSite={!!row.mobility} t={t} />
              ))}
            </div>
          )}

          {/* ── История обслуживания ── */}
          {row && !isNew && !editing && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                            letterSpacing: '.05em', marginBottom: 8 }}>
                {t('equipment.history')}
              </div>
              {completedTimeline.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>
                  {t('equipment.noHistory')}
                </div>
              ) : completedTimeline.map((w) => (
                <TimelineRow key={w.id} w={w} loc={loc} onOpenTask={onOpenTask} onClose={onClose}
                             propertyId={row.property_id} showSite={!!row.mobility} t={t} />
              ))}
              {/* Переносная единица работала на Объектах вне области данных
                  пользователя — сервер отдаёт только их число, без деталей.
                  Строка честно говорит, что история неполная. */}
              {row.timeline_hidden_count > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text3)', padding: '4px 2px' }}>
                  {t('equipmentMove.hiddenWorks', { count: row.timeline_hidden_count })}
                </div>
              )}
            </div>
          )}

          {/* ── Гейт «Готова»: причина отказа и аварийный выпуск без QA ── */}
          {gate && (
            <div className="card" style={{ padding: 12, marginBottom: 12, border: '1px solid #c77700' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('equipmentGate.title')}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
                {t(`equipmentGate.reason.${gate.reason}`, { count: gate.open_defects || 0 })}
              </div>
              {gate.task_id && onOpenTask && (
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: 8 }}
                  data-ph-capture="equipment_gate_open_check"
                  onClick={() => { onOpenTask(gate.task_id, row?.property_id); onClose && onClose(); }}>
                  {t('equipmentGate.openCheck')}
                </button>
              )}
              {gate.can_override && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>{t('equipmentGate.overrideHint')}</div>
                  <input className="form-input" value={overrideReason} maxLength={500}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder={t('equipmentGate.overridePlaceholder')} />
                  <button type="button" className="btn btn-danger btn-sm" style={{ marginTop: 8 }}
                    disabled={statusBusy || !overrideReason.trim()}
                    data-ph-capture="equipment_release_override"
                    onClick={() => releaseStatus('ready', overrideReason.trim())}>
                    {t('equipmentGate.overrideBtn')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Перемещения (только у организаций с включённым перемещением
              или у единицы, которая уже ездила) ── */}
          {row && !isNew && !editing && row.mobility && row.in_scope !== false
            && (row.mobility.is_mobile || (row.mobility.movements || []).some((m) => m.kind !== 'start')) && (
            <MobilitySection mobility={row.mobility} loc={loc} t={t}
                             commands={flowCommands}
                             onMove={(mode) => setMoving(mode || true)} />
          )}

          {/* ── Наклейки на этой единице ──
              Их может быть несколько: поверх затёртого стикера клеят новый, а
              старый не гасят — он ещё читается. Погашенные показываем тоже,
              иначе непонятно, почему код с наклейки больше не открывается. */}
          {row && !isNew && (row.tags || []).length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                            letterSpacing: '.05em', marginBottom: 8 }}>
                {t('equipment.tagsTitle')}
              </div>
              {row.tags.map((tag) => (
                <div key={tag.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                  opacity: tag.revoked_at ? 0.55 : 1,
                }}>
                  <span style={{
                    fontFamily: 'monospace', fontSize: 13, fontWeight: 600,
                    textDecoration: tag.revoked_at ? 'line-through' : 'none',
                  }}>
                    {formatCode(tag.token)}
                  </span>
                  {tag.revoked_at && (
                    <span className="badge" style={{ background: 'var(--red)', color: '#fff' }}>
                      {t('equipmentTags.stRevoked')}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {row.can_manage_tags && !tag.revoked_at && (
                    <>
                      {/* Основной вход в допечатку: с объекта приходит «одна
                          наклейка отвалилась», и человек знает ЕДИНИЦУ, а не
                          код. Искать его в реестре из пятисот строк ради
                          одного стикера — работа на пустом месте. */}
                      {sheet.readyFile ? (
                        <button className="btn btn-sm btn-primary" data-ph-capture="equipment_tag_reprint_share_card"
                                onClick={sheet.share}>
                          {t('equipmentTags.sheetShare')}
                        </button>
                      ) : (
                        <button data-ph-capture="equipment_tag_reprint_card"
                                style={{ ...tagIconBtn(false), opacity: sheet.busy ? 0.5 : 1 }}
                                disabled={sheet.busy}
                                title={sheet.busy ? t('equipmentTags.sheetBuilding') : t('equipmentTags.reprintOne')}
                                onClick={async () => {
                                  const ok = await sheet.get(() => api.equipmentTagSheetFor([tag.token]));
                                  if (ok) track('tag_batch_print', { partial: true, count: 1, source: 'card' });
                                  else if (sheet.error) setError(sheet.error);
                                }}>
                          <Icon name="printer" size={14} />
                        </button>
                      )}
                      <button data-ph-capture="equipment_tag_revoke_card"
                              style={{ ...tagIconBtn(true), opacity: revoking === tag.id ? 0.5 : 1 }}
                              disabled={revoking === tag.id}
                              title={t('equipmentTags.revoke')}
                              onClick={() => revokeTag(tag)}>
                        <Icon name="trash" size={14} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Паспорт ── */}
          {row && (editing || isNew) ? (
            <>
              {/* ── Наклейка, отсканированная прямо из этой формы (ещё не
                  сохранённая единица) ──
                  У существующей единицы код привязывается сразу по скану
                  (onTagResolved), и появится в «Наклейках» выше после load() —
                  отдельный чип тут не нужен. У новой — привязка ждёт
                  «Сохранить», и человек должен видеть, что код уже взят. */}
              {isNew && localTagToken && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', marginBottom: 12,
                  borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg2)',
                }}>
                  <Icon name="scan" size={14} style={{ flexShrink: 0, color: 'var(--text3)' }} />
                  <span style={{ fontSize: 12, flex: 1 }}>
                    {t('equipment.tagPending', { code: formatCode(localTagToken) })}
                  </span>
                  <button type="button" data-ph-capture="equipment_scan_pending_clear"
                          style={{ ...tagIconBtn(true), width: 24, height: 24 }}
                          onClick={() => setLocalTagToken(null)}>
                    <Icon name="x" size={12} />
                  </button>
                </div>
              )}

              {/* ── Фото общего вида (до 4) и шильдика ──
                  Первым блоком в режиме создания: заполнение по фото — это
                  основной путь, а ручной ввод шести полей мелким шрифтом —
                  запасной. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <EquipmentPhotoGrid
                  label={t('equipment.fieldPhoto')}
                  hint={t('equipment.photoHint')}
                  photos={photos}
                  busy={uploading}
                  onFiles={onPhotoFiles}
                  onRemove={removePhoto}
                  onOpen={(p) => setViewerSrc(p.full || p.preview)}
                />
                <PhotoSlot
                  label={t('equipment.fieldPlate')}
                  hint={t('equipment.plateHint')}
                  preview={platePreview}
                  busy={uploading === 'plate'}
                  onFiles={onPlateFiles}
                  onClear={clearPlate}
                  onOpen={platePreview ? () => setViewerSrc(platePreview) : undefined}
                  t={t}
                />
              </div>

              {/* Кнопка распознавания привязана к шильдику, а не к общему виду:
                  гонять модель по фотографии холодильника целиком — это счёт
                  без результата. */}
              {plateKey && canOcr && (
                <button type="button" className="btn btn-primary btn-sm"
                        data-ph-capture="equipment_plate_ocr"
                        style={{ width: '100%', marginTop: 4, marginBottom: 10 }}
                        onClick={recognize} disabled={ocrState === 'running'}>
                  {ocrState === 'running' ? t('equipment.ocrRunning')
                    : ocrState === 'done' ? t('equipment.ocrAgain')
                    : t('equipment.ocrFill')}
                </button>
              )}

              {ocrState !== 'idle' && ocrState !== 'running' && (
                <div style={{
                  marginBottom: 12, fontSize: 12,
                  color: ocrState === 'done' ? 'var(--green, #0f9d58)' : 'var(--text3)',
                }}>
                  {ocrState === 'done' && t('equipment.ocrDone')}
                  {ocrState === 'empty' && t('equipment.ocrEmpty')}
                  {ocrState === 'failed' && t('equipment.ocrFailed')}
                  {ocrState === 'limit' && t('equipment.ocrLimit')}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">{t('equipment.fieldName')} *</label>
                <input className="form-input" value={form.name} onChange={(e) => set('name', e.target.value)}
                       placeholder={t('equipment.namePlaceholder')}
                       // Автофокус — только при заведении новой единицы: там
                       // пустое обязательное поле и есть смысл сразу давать
                       // курсор. При «Редактировать» существующей карточки
                       // название уже заполнено — незачем сразу открывать
                       // клавиатуру и сдвигать модал на мобильном раньше, чем
                       // человек попросил печатать.
                       autoFocus={isNew}
                       style={ocrFields.has('name') ? OCR_HL : undefined} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('equipment.fieldType')}</label>
                <select className="form-select" value={form.type} onChange={(e) => set('type', e.target.value)}>
                  <option value="">{t('equipment.selectType')}</option>
                  {equipmentTypes.map((et) => (
                    <option key={et.key} value={et.key}>{equipmentTypeLabel(et, i18n.language)}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">{t('equipment.fieldManufacturer')}</label>
                  <input className="form-input" value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)}
                         style={ocrFields.has('manufacturer') ? OCR_HL : undefined} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('equipment.fieldModel')}</label>
                  <input className="form-input" value={form.model} onChange={(e) => set('model', e.target.value)}
                         style={ocrFields.has('model') ? OCR_HL : undefined} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('equipment.fieldSerial')}</label>
                  <input className="form-input" value={form.serial_no} onChange={(e) => set('serial_no', e.target.value)}
                         style={ocrFields.has('serial_no') ? OCR_HL : undefined} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('equipment.fieldInstalled')}</label>
                  <input className="form-input" type="date" value={form.installed_at} onChange={(e) => set('installed_at', e.target.value)}
                         onBlur={syncDate('installed_at')}
                         style={ocrFields.has('installed_at') ? OCR_HL : undefined} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('equipment.fieldWarranty')}</label>
                  <input className="form-input" type="date" value={form.warranty_until} onChange={(e) => set('warranty_until', e.target.value)}
                         onBlur={syncDate('warranty_until')} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('equipment.fieldNotes')}</label>
                <textarea className="form-textarea" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
              {/* Учёт перемещений — свойство самой единицы, поэтому здесь, в
                  правке карточки, а не в блоке перемещений: там галочка
                  занимала место каждый раз, хотя меняют её один раз. Только у
                  организаций с включённым перемещением и с правом правки. */}
              {!isNew && row.mobility?.can_toggle_mobile && (
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', marginBottom: 12,
                  borderRadius: 8, background: 'var(--bg2)', cursor: saving ? 'default' : 'pointer',
                }}>
                  <input type="checkbox" checked={!!form.is_mobile} disabled={saving}
                         data-ph-capture="equipment_mobile_toggle"
                         onChange={(e) => set('is_mobile', e.target.checked)}
                         style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0 }} />
                  <span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t('equipmentMove.mobileLabel')}</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                      {t('equipmentMove.mobileHint')}
                    </span>
                  </span>
                </label>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text2)', display: 'grid', gap: 5 }}>
              {form.type && <div>{t('equipment.fieldType')}: <b style={{ color: 'var(--text)' }}>{equipmentTypeLabel(equipmentTypes.find((et) => et.key === form.type), i18n.language) || form.type}</b></div>}
              {form.manufacturer && <div>{t('equipment.fieldManufacturer')}: <b style={{ color: 'var(--text)' }}>{form.manufacturer}</b></div>}
              {form.model && <div>{t('equipment.fieldModel')}: <b style={{ color: 'var(--text)' }}>{form.model}</b></div>}
              {form.serial_no && <div>{t('equipment.fieldSerial')}: <b style={{ color: 'var(--text)' }}>{form.serial_no}</b></div>}
              {form.warranty_until && <div>{t('equipment.fieldWarranty')}: <b style={{ color: 'var(--text)' }}>{form.warranty_until}</b></div>}
              {shownNotes && <div style={{ marginTop: 4 }}>{shownNotes}</div>}
            </div>
          )}

          {row && error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{error}</div>}
        </div>

        <div className="modal-footer">
          {row && (editing || isNew) ? (
            <>
              {/* Слева, отдельно от пары Отмена/Сохранить (marginRight: auto
                  раздвигает флекс-ряд) — это не действие над формой, а
                  самостоятельный шаг: привязать наклейку можно в любой
                  момент заполнения, не отвлекаясь на «сохранить, потом
                  сканировать». Право то же, что и на сам бэкенд-роут бинда —
                  equipment.create, а не equipment.edit. */}
              {can('equipment.create') && (
                <button type="button" className="btn btn-sm" data-ph-capture="equipment_scan_from_card"
                        style={{ marginRight: 'auto' }}
                        onClick={() => setScanning(true)} disabled={saving || scanBusy}>
                  <Icon name="scan" size={12} /> {t('equipment.scanQrButton')}
                </button>
              )}
              <button className="btn" onClick={(isNew || startEditing) ? onClose : () => setEditing(false)} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" data-ph-capture="equipment_save" onClick={save} disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button className="btn" onClick={onClose}>{t('common.close')}</button>
              {canEdit && (
                <button className="btn btn-primary" data-ph-capture="equipment_edit" onClick={() => setEditing(true)}>
                  {t('common.edit')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Просмотрщик — position: fixed с z-index 3000, поэтому лежит поверх
          модалки независимо от места в разметке. Клик по нему не должен
          добираться до backdropClose родителя, иначе закрытие фото уносит
          с собой форму. */}
      {viewerSrc && (
        <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <FullscreenViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />
        </div>
      )}

      {/* Сканер — тем же приёмом, что просмотрщик выше: свой z-index 3000
          (см. EquipmentScanner), stopPropagation глушит backdropClose родителя.
          Разбор результата — onTagResolved, он же решает, привязать сразу
          или отложить до «Сохранить». */}
      {scanning && (
        <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <EquipmentScanner onResolved={onTagResolved} onClose={() => setScanning(false)} />
        </div>
      )}

      {/* Форма Работы поверх карточки — через портал в document.body.
          ⚠️ Раньше рендерилась прямо здесь, вложенной в наш `.modal-overlay`.
          У обоих модалов общий CSS-класс с `backdrop-filter: blur()` — а
          вложенный `position: fixed` внутри предка с `backdrop-filter` на
          iOS Safari перестаёт позиционироваться от вьюпорта и ловит тапы
          мимо себя: на телефоне это выглядело как «Новая работа» просто
          закрывает карточку, вместо того чтобы открыть форму — форма мелькала
          на один кадр и тут же схлопывалась вместе с родителем. Портал делает
          TaskModal прямым потомком body, вне блюра предка, — тот же приём,
          что у FabSpeedDial (см. его шапку).

          Свой обработчик pointerdown/click остаётся: React пробрасывает
          события по ДЕРЕВУ КОМПОНЕНТОВ, а не по DOM, так что портал сам по
          себе не помешал бы клику внутри формы всплыть до backdropClose
          карточки и закрыть её вместе с формой.

          Оборудование передаём заранее выбранным: TaskModal копит его в
          pendingEquipment и досылает привязку сразу после создания Работы —
          у новой Работы ещё нет id. */}
      {moving && row && createPortal(
        <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <EquipmentMoveDialog
            equipment={row}
            currentPropertyId={row.property_id}
            mode={typeof moving === 'string' ? moving : undefined}
            onClose={() => setMoving(false)}
            onMoved={() => {
              setMoving(false);
              // Единица уехала — на прежнем Объекте её больше нет: список
              // парка под карточкой должен перечитаться. Отдельный колбэк, а
              // не onSaved: у родителей он закрывает карточку или трактует
              // аргумент как id новой единицы (EquipmentScanFlow).
              onChanged && onChanged(row.id);
              load();
            }}
          />
        </div>,
        document.body
      )}

      {creatingTask && row && createPortal(
        <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          {/* Заголовок НЕ подставляем именем единицы. «Посудомоечная машина» —
              это не название работы, а название железа; такой дефолт человек
              всё равно стирает, зато половина забудет и в списке Работ появится
              строка ни о чём. Само оборудование уже выбрано в форме и видно. */}
          <TaskModal
            // ⚠️ row.property_id — основной источник (всегда приходит с
            // карточкой), propertyId-проп — подстраховка на случай, если он
            // вдруг пуст. Без фиксированного Объекта форма Работы вместо
            // готового выезда открывает выбор из всех Объектов — ровно то,
            // от чего сканирование должно избавлять.
            propertyId={row.property_id || propertyId}
            initialEquipmentIds={[row.id]}
            prefill={taskPrefill}
            onClose={() => { setCreatingTask(false); setTaskPrefill(null); }}
            onSave={() => {
              if (taskPrefill?.type === 'emergency') track('equipment_breakdown_reported');
              setCreatingTask(false); setTaskPrefill(null); load();
              // Работа на единицу меняет её статус (экстренная → «Неисправна»,
              // любая на Складе → «На обслуживании») — список под карточкой
              // должен узнать об этом сразу, а не после перехода.
              onChanged && onChanged(row.id);
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
